<template>
    <v-card>
        <v-toolbar flat dense>
            <v-tabs v-model="tab">
                <v-tab v-if="$accessable(['serviceLists'])">
                    Group
                </v-tab>
                <v-tab v-if="$accessable(['usersCreate', 'usersRemove'])">
                    User Manager
                </v-tab>
            </v-tabs>
        </v-toolbar>

        <v-tabs-items v-model="tab">
            <v-tab-item v-if="$accessable(['serviceLists'])">
                <feathers-vuex-find service="bh-roles" :query="{}">
                    <v-card slot-scope="{ items: desserts, isFindPending }">
                        <v-card-title>
                            Group Lists
                            <v-divider class="mx-4" inset vertical></v-divider>
                            <v-spacer></v-spacer>
                            <v-dialog v-model="createRoleDialog" width="500" persistent>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn color="green" dark small v-bind="attrs" v-on="on"><v-icon left>mdi-plus</v-icon> Add New Role</v-btn>
                                </template>
                                <v-card>
                                    <v-card-title>
                                        Add New Role
                                    </v-card-title>
                                    <v-card-text>
                                        <v-container>
                                            <v-row>
                                                <v-text-field v-model="roleCreationForm.name" label="Role Name" single-line :disabled="roleCreationForm.loading"></v-text-field>
                                            </v-row>
                                        </v-container>
                                    </v-card-text>
                                    <v-divider></v-divider>
                                    <v-card-actions>
                                        <v-spacer></v-spacer>
                                        <v-btn small color="red" text @click="createRoleDialog = false">Cancel</v-btn>
                                        <v-btn small color="primary" text :loading="roleCreationForm.loading" @click="createNewRole">Save</v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-dialog>
                        </v-card-title>
                        <v-data-table :headers="roleHeaders" :loading="isFindPending" :items="desserts" :search="searchRole">
                            <template v-slot:item.role_name="{ item }">
                                <b>{{ item.name }}</b>
                            </template>
                            <template v-slot:item.actions="{ item }">
                                <v-dialog v-model="item.dialog" width="1000" persistent>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn color="primary" small dark v-bind="attrs" v-on="on" outlined>
                                            <v-icon left>mdi-pencil</v-icon> Manage Service
                                        </v-btn>
                                    </template>
                                    <role-services-manager-table :target-role="item.name" @close="item.dialog = false" @success="successMessage = true" @error="onError" />
                                </v-dialog>
                            </template>
                        </v-data-table>
                    </v-card>
                </feathers-vuex-find>
            </v-tab-item>
            <v-tab-item v-if="$accessable(['usersCreate', 'usersRemove'])">
                <feathers-vuex-find service="users" :query="dataQuery" :fetch-query="fetchQuery" watch="query">
                    <v-card slot-scope="{ items: desserts, pagination: serverPagination, isFindPending }">
                        <v-card-title>
                            User Manager
                            <v-spacer></v-spacer>
                            <v-text-field v-model="searchUser" append-icon="mdi-magnify" label="Search" single-line hide-details></v-text-field>
                        </v-card-title>
                        <v-data-table 
                            :headers="userRolesHeaders"
                            :loading="isFindPending"
                            :items="isFindPending ? [] : desserts"
                            :options.sync="pagination"
                            :server-items-length="!serverPagination ? 0 : serverPagination.mostRecent.total"
                            loading-text="Fetching Data..."
                            single-expand
                            show-expand
                        >
                            <template v-slot:item.fullname="{ item }">
                                <a :href="`https://www.breakinghits.app/${item.url}`" style="color: #000;" target="__site__"><b>{{ item.fname }} {{ item.lname }}</b></a> ({{ item.email }})<br />
                                <i style="color: gray;">{{ item.url }}</i>
                            </template>
                            <template v-slot:item.date_added="{ item }">
                                {{ item.date_added && dateFormat(item.date_added) || 'N/A' }}
                            </template>
                            <template v-slot:item.tiktok_followers_count="{ item }">
                                <a class="tiktok_count_url" :href="item.tiktok_url" target="_tiktok_" v-if="!!item.tiktok_followers_count" style="color: #000;">{{ numberToReadable(item.tiktok_followers_count) }}</a>
                                <div v-else>-</div>
                            </template>
                            <template v-slot:item.instagram_followers_count="{ item }">
                                <a class="instagram_count_url" :href="autoCompleteInstagramUrl(item.instagram_url)" target="_instagram_" v-if="!!item.instagram_followers_count" style="color: #000;">{{ numberToReadable(item.instagram_followers_count) }}</a>
                                <div v-else>-</div>
                            </template>
                            <template v-slot:item.verified="{ item }">
                                <v-chip x-small v-if="item.verified === 'PENDING'" class="ma-2" color="#d5d5d5" text-color="white">Pending</v-chip>
                                <v-chip x-small v-else class="ma-2" color="green" text-color="white">Verified</v-chip>
                            </template>
                            <template v-slot:item.current_role="{ item }">
                                <v-chip x-small v-if="item.roles.length === 0" class="ma-2" color="blue-grey" text-color="white">Influencer</v-chip>
                                <v-chip x-small v-for="(role, index) in item.roles" class="ma-2" color="green" text-color="white" :key="index" style="text-transform: capitalize;">{{ role.role.name }}</v-chip>
                            </template>
                            <template v-slot:expanded-item="{ headers, item }">
                                <td :colspan="headers.length">
                                    <v-row>
                                        <v-col align="end">
                                            <v-btn v-if="item.verified === 'PENDING'" class="mr-2" small color="light-blue darken-3" rounded dark @click="onActivateUser(item)">
                                                <v-icon left>mdi-account-sync</v-icon>
                                                Activate User
                                            </v-btn>

                                            <add-new-points-dialog :key="`add-points-${item.id}`" :item="item" @success="successMessage = true" @error="onError" />

                                            <!-- Change Password -->
                                            <v-dialog v-model="item.changePasswordDialog" width="350" persistent>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn class="mr-2" small color="purple" rounded dark v-bind="attrs" v-on="on">
                                                        <v-icon left>mdi-lock-reset</v-icon>
                                                        Change Password
                                                    </v-btn>
                                                </template>
                                                <v-card>
                                                    <v-card-title>
                                                        Change User Password
                                                    </v-card-title>
                                                    <v-card-text>
                                                        <v-text-field 
                                                            :type="modalPasswordShow ? 'text' : 'password'" 
                                                            v-model="item.password" 
                                                            label="New Password"
                                                            :append-icon="modalPasswordShow ? 'mdi-eye' : 'mdi-eye-off'"
                                                            @click:append="() => (modalPasswordShow = !modalPasswordShow)"
                                                            :disabled="modalPasswordLoading"
                                                        ></v-text-field>
                                                    </v-card-text>
                                                    <v-card-actions>
                                                        <v-spacer></v-spacer>
                                                        <v-btn v-if="!modalPasswordLoading" small color="red" text @click="item.changePasswordDialog = false">Cancel</v-btn>
                                                        <v-btn small color="primary" text @click="changePassword(item)" :loading="modalPasswordLoading">Save</v-btn>
                                                    </v-card-actions>
                                                </v-card>
                                            </v-dialog>

                                            <role-manager-dialog :key="`role-manager-${item.id}`" :item="item" @success="successMessage = true" @error="onError" />
                                            <permissions-dialog :key="`permissions-${item.id}`" :item="item" @success="successMessage = true" @error="onError" />
                                        </v-col>
                                    </v-row>
                                </td>
                            </template>
                        </v-data-table>
                    </v-card>
                </feathers-vuex-find>
            </v-tab-item>
        </v-tabs-items>
        <v-snackbar top v-model="successMessage" color="success">
            Successfully Saved!
        </v-snackbar>
        <v-snackbar top v-model="error.show" color="red">
            {{ error.message }}
        </v-snackbar>
    </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import moment from 'moment';
import RoleServicesManagerTable from './components/RoleServicesManagerTable';
import RoleManagerDialog from './components/RoleManagerDialog';
import PermissionsDialog from './components/PermissionsDialog';
import AddNewPointsDialog from './components/AddPointsDialog';

export default {
    data() {
        return {
            tab: 0,
            searchRole: '',
            searchUser: '',
            createRoleDialog: false,
            successMessage: false,
            error: {
                show: false,
                message: ''
            },
            roleHeaders: [
                {
                    text: 'Role Name',
                    align: 'start',
                    sortable: false,
                    value: 'role_name'
                },
                {
                    text: 'Actions',
                    align: 'end',
                    sortable: false,
                    value: 'actions'
                },
            ],
            userRolesHeaders: [
                {
                    text: 'ID',
                    align: 'start',
                    sortable: true,
                    value: 'id'
                },
                {
                    text: 'Full Name',
                    align: 'start',
                    sortable: true,
                    value: 'fullname'
                },
                {
                    text: 'TikTok Followers Count',
                    align: 'center',
                    sortable: true,
                    value: 'tiktok_followers_count'
                },
                {
                    text: 'Instagram Followers Count',
                    align: 'center',
                    sortable: true,
                    value: 'instagram_followers_count'
                },
                {
                    text: 'Date Registered',
                    align: 'end',
                    sortable: true,
                    value: 'date_added'
                },
                {
                    text: 'Status',
                    align: 'center',
                    sortable: false,
                    value: 'verified'
                },
                {
                    text: 'Current Role',
                    align: 'end',
                    sortable: false,
                    value: 'current_role'
                },
                {
                    text: 'Actions',
                    align: 'end',
                    value: 'data-table-expand'
                }
            ],
            roleCreationForm: {
                name: null,
                loading: false
            },
            pagination: {
                page: 1,
                itemsPerPage: 10,
                sortBy: ['id'],
                sortDesc: [true]
            },
            query: { /*id: { $nin: [this.user.id] }*/ },
            modalPasswordShow: false,
            modalPasswordLoading: false,
        };
    },
    computed: {
        extendedQuery() {
            if(this.user) {
                return {
                    id: { $nin: [this.user.id] }
                };
            }
            return null;
        },
        searchQuery() {
            if(this.searchUser) {
                if(isNaN(parseInt(this.searchUser))) {
                    return {
                        $or: [
                            { fname: { $like: `%${this.searchUser}%` } },
                            { lname: { $like: `%${this.searchUser}%` } },
                            { email: { $like: `%${this.searchUser}%` } },
                            { url: { $like: `%${this.searchUser}%` } }
                        ]
                    };
                } else {
                    return {
                        id: this.searchUser
                    };
                }
                
            }
            return null;
        },
        searchDataQuery() {
            if(this.searchUser) {
                if(isNaN(parseInt(this.searchUser))) {
                    return {
                        $or: [
                            { fname: { $regex: new RegExp(`.*${this.escapeRegex(this.searchUser)}.*`, 'gi') } },
                            { lname: { $regex: new RegExp(`.*${this.escapeRegex(this.searchUser)}.*`, 'gi') } },
                            { email: { $regex: new RegExp(`.*${this.escapeRegex(this.searchUser)}.*`, 'gi') } },
                            { url: { $regex: new RegExp(`.*${this.escapeRegex(this.searchUser)}.*`, 'gi') } },
                        ]
                    };
                } else {
                    return {
                        id: parseInt(this.searchUser)
                    };
                }
            }
            return null;
        },
        sortingQuery() {
            if(this.pagination.sortBy.length !== 0) {
                const $sort = {};
                this.pagination.sortBy.map((item, index) => {
                    switch(item) {
                        case 'fullname':
                            $sort.fname = this.pagination.sortDesc[index] ? -1 : 1;
                            $sort.lname = this.pagination.sortDesc[index] ? -1 : 1;
                            break;
                        default:
                            $sort[item] = this.pagination.sortDesc[index] ? -1 : 1;
                    }
                    
                });
                return { $sort };
            }
            return null;
        },
        dataQuery() {
            return {
                ...this.query,
                ...this.extendedQuery,
                ...this.searchDataQuery,
                $limit: this.pagination.itemsPerPage,
                $skip: (this.pagination.page-1) * this.pagination.itemsPerPage,
                ...this.sortingQuery
            };
        },
        fetchQuery() {
            return Object.assign({}, {
                ...this.query,
                ...this.extendedQuery,
                $populate: true
            }, {
                ...this.searchQuery,
                $limit: this.pagination.itemsPerPage,
                $skip: (this.pagination.page-1) * this.pagination.itemsPerPage,
                ...this.sortingQuery
            });
        },
        ...mapState('auth', ['user'])
    },
    methods: {
        async createNewRole() {
            this.roleCreationForm.loading = true;
            try {
                await this.createNewRoles(this.roleCreationForm);
                this.successMessage = true;
                this.createRoleDialog = false;
            } catch(err) {
                this.error.show = true;
                this.error.message = err.message;
            } finally {
                this.roleCreationForm.loading = false;
            }
        },
        onError(err) {
            this.error.show = true;
            this.error.message = err.message;
        },
        async onActivateUser(item) {
            try {
                await this.findEmail({
                    query: { email: item.email }
                }).then((result) => {
                    Promise.all(result.data.map(async (item) => {
                        this.activateUser([item.id, {
                            status: 'VERIFIED'
                        }])
                    }));
                });
                this.successMessage = true;
                item.verified = 'VERIFIED';
            } catch(err) {
                this.error.show = true;
                this.error.message = err.message;
            }
        },
        paginate(pagination) {
            console.log(pagination);
        },
        escapeRegex(string) {
            return string.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
        },
        async changePassword(item) {
            try {
                this.modalPasswordLoading = true;
                await item.update();
                this.successMessage = true;
                this.modalPasswordShow = false;
                item.changePasswordDialog = false;
            } catch(err) {
                this.error.show = true;
                this.error.message = err.message;
            } finally {
                this.modalPasswordLoading = false;
            }
        },
        dateFormat(date) {
            return moment(date).format('lll');
        },
        numberToReadable(data) {
            if(data.toString().length >= 7) {
                return ((parseInt(data) / 1000000).toFixed(1) + 'M').replace(/\.0/g, '');
            } else if(data.toString().length >= 4) {
                return ((parseInt(data) / 1000).toFixed(1) + 'K').replace(/\.0/g, '');
            } else {
                return data;
            }
        },
         autoCompleteInstagramUrl(url) {
            const cleanUrl = url.replace(/^http(s?)\:\/\//gi, '').replace(/www\./gi, '').replace(/instagram.com\//gi, '');
            return `https://www.instagram.com/${cleanUrl}`;
        },
        ...mapActions('bh-roles', { createNewRoles: 'create' }),
        ...mapActions('bh-email-verification', { findEmail: 'find', activateUser: 'patch' })
    },
    components: {
        RoleServicesManagerTable,
        RoleManagerDialog,
        PermissionsDialog,
        AddNewPointsDialog,
    }
};
</script>

<style>
.tiktok_count_url, .instagram_count_url {
    text-decoration: none;
}
.tiktok_count_url:hover, .instagram_count_url:hover {
    text-decoration: underline;
}
</style>